import { User } from 'src/app/authentication/model/user.model';
import { ColorTypology } from './typology.model';
import { ControlSummary } from '../../control/model/control.model';
import { TechnicalInstallation } from './technical-installations.model';

export const today = 'today';

export const HISTORY_TYPE = {
  typology: 'typology',
  control: 'control',
  status: 'status',
  itech: 'itech',
  ecab: 'ecab',
} as const;
export type HistoryType = typeof HISTORY_TYPE[keyof typeof HISTORY_TYPE];

export const CONTROL_STATUS = {
  completed: 'COMPLETED',
  ongoing: 'ONGOING',
  orphan: 'ORPHAN',
} as const;
export type ControlStatus = typeof CONTROL_STATUS[keyof typeof CONTROL_STATUS];

export const CONTROL_TYPE = {
  periodic: 'PERIODIC',
  reception: 'RECEPTION',
} as const;
export type ControlType = typeof CONTROL_TYPE[keyof typeof CONTROL_TYPE];

export const ASSESSMENT_STATUS = {
  approved: 'APPROVED',
  ongoing: 'ONGOING',
  canceled: 'CANCELED',
  approval: 'APPROVAL',
} as const;
export type AssessmentStatus = typeof ASSESSMENT_STATUS[keyof typeof ASSESSMENT_STATUS];

export interface ItechEntry {
  id: number;
  status: string;
  modificationDate: string;
  installationDate?: string;
  user?: User;
  technicalInstallation?: TechnicalInstallation;
  isFirstForThisItech?: boolean;
}

export interface ControlEntry {
  id: number;
  date: string;
  user: User;
  status: ControlStatus;
  isCompliant: boolean;
  defectCount: number;
  reportFileName?: string;
}

export interface TypologyEntry {
  previousColor?: ColorTypology;
  color: ColorTypology;
  source: string;
  date: string;
  affectationKeys: string[];
  justification: string;
  user: User;
}

export interface StatusEntry {
  source: string;
  date: string;
  justification: string;
  status: string;
  user: User;
}

export interface EcabReference {
  egid: string;
  sibatId: string;
  npa: string;
  icareplusIds: string[];
  gpsCoordinates: { lat: string; long: string }[];
}

export interface EcabEntry {
  id: number;
  type: 'BUILDING' | 'DOSSIER';
  referenceId: string;
  referenceLabel: string;
  startedAt: string;
  completedAt: string | null;
  status: string;
  source: string | null;
  processingTheme: string | null;
  applicantType: string | null;
  requestDescription: string | null;
  responseDescription: string | null;
  userEmail: string | null;
  references: EcabReference[];
}

export interface EditHistory {
  control: ControlEntry[];
  typology: TypologyEntry[];
  status: StatusEntry [];
  itech: ItechEntry[];
  ecabActivities?: EcabEntry[];
}

export interface EditHistoryWithValidatedControl {
  control: ControlEntry[];
  typology: TypologyEntry[];
  status: StatusEntry [];
  validatedControl: ControlSummary;
}

export interface HistoryEntry {
  data: ControlEntry | TypologyEntry | StatusEntry | ItechEntry | EcabEntry;
  date: string;
  type: HistoryType;
}

export const typologyData = (entry: TypologyEntry, previousColor?: ColorTypology): HistoryEntry => ({
  data: { ...entry, previousColor },
  date: entry.date,
  type: HISTORY_TYPE.typology,
});

export const controlData = (entry: ControlEntry): HistoryEntry => ({
  data: entry,
  date: entry.date,
  type: HISTORY_TYPE.control,
});

export const statusData = (entry: StatusEntry): HistoryEntry => ({
  data: entry,
  date: entry.date,
  type: HISTORY_TYPE.status,
});

export const itechData = (entry: ItechEntry): HistoryEntry => ({
  data: entry,
  date: entry.modificationDate,
  type: HISTORY_TYPE.itech,
});

export const ecabData = (entry: EcabEntry): HistoryEntry => ({
  data: entry,
  date: entry.startedAt,
  type: HISTORY_TYPE.ecab,
});
